/* RESPONSIBLE TEAM: team-reporting */

import { ChartDefinition, ChartSchema, Timezone } from '@/features/report-snapshots/api/schema';
import { I18n } from '@lingui/core';
import { assignColorToSeriesItemViewBy } from '@/features/report-snapshots/components/charts/utils/chart-series-colors';
import { formatTimestamp } from '@/features/report-snapshots/formatters/timestamp';
import { formatSeconds } from '@/features/report-snapshots/formatters/duration';
import { REPORTING_CONVERSATION_RATINGS } from '@/features/report-snapshots/constants';
import { normalizeCellValue } from '@/features/report-snapshots/utils/common';
import { formatNumber } from '@/features/report-snapshots/formatters/number';

export interface PieChartSeriesData {
  name: string;
  y: number | null;
  formattedValue: string;
  color?: string;
}

export function getPieSeries(
  schema: ChartSchema,
  chartDefinition: ChartDefinition,
  timezone: Timezone,
  i18n: I18n,
): PieChartSeriesData[] {
  const rawData = extractPieDataPoints(schema, chartDefinition, timezone, i18n);
  const formattedData = addFormattedValues(rawData, schema, chartDefinition, i18n);

  if (chartDefinition.viewBy === 'time') {
    formattedData.reverse();
  }

  return formattedData;
}

function extractPieDataPoints(
  schema: ChartSchema,
  chartDefinition: ChartDefinition,
  timezone: Timezone,
  i18n: I18n,
): PieChartSeriesData[] {
  const isViewByTime = chartDefinition.viewBy == 'time';

  return schema.values.map((value, index) => {
    const cellValue = value[1];
    let label = schema.values[index]['0'] as string;

    if (isViewByTime) {
      label = formatTimestamp(
        chartDefinition.viewByTimeInterval ?? 'week',
        label as unknown as number,
        timezone,
        i18n,
      );
    }

    if (label in REPORTING_CONVERSATION_RATINGS) {
      label = i18n._(
        REPORTING_CONVERSATION_RATINGS[label as keyof typeof REPORTING_CONVERSATION_RATINGS],
      );
    }

    const columnDefinition = schema.columnDefinitions[1];
    const numericValue = normalizeCellValue(cellValue, columnDefinition);

    return {
      name: label,
      y: numericValue,
      formattedValue: '',
    };
  });
}

function addFormattedValues(
  data: { name: string; y: number | null }[],
  schema: ChartSchema,
  chartDefinition: ChartDefinition,
  i18n: I18n,
) {
  const columnDefinition = schema.columnDefinitions[1];
  const metricType = columnDefinition.unit;

  // Calculate total sum once for field metric
  const totalSum = data.reduce((acc, curr) => acc + (curr.y ?? 0), 0);
  const numberFormatter = formatNumber(i18n);

  const dataWithFormatting = data.map((item) => {
    let formattedValue: string;
    const rawY = item.y ?? 0;

    switch (metricType) {
      case 'percent':
        formattedValue = `${(rawY * 100).toFixed(1)}%`;
        break;

      case 'seconds':
        formattedValue = formatSeconds(rawY);
        break;

      case 'ratio':
        formattedValue = rawY.toFixed(1);
        break;

      default: {
        // For field metric: show value formatted and percentage
        const percentage = ((rawY / totalSum) * 100).toFixed(2);
        const formattedY =
          rawY % parseInt(String(rawY)) === 0 ? numberFormatter(rawY) : rawY.toFixed(2);
        formattedValue = `${percentage}% (${formattedY})`;
        break;
      }
    }

    return {
      ...item,
      formattedValue,
    };
  });

  // assign color to the series
  dataWithFormatting.forEach((item) => {
    assignColorToSeriesItemViewBy(item, chartDefinition);
  });

  return dataWithFormatting;
}
